import React from 'react';
import { StandaloneSearchBoxes } from '../../common/StandaloneSearchBoxes';
import { SchoolSuggestion, TeacherSuggestion } from '../../search/types/typeaheadSuggestions';
import { SchoolZipCodeSearch } from '../../common/SchoolZipCodeSearch.js';
import { GeoLocationPlace } from '../../common/location/types';
import { convertToSchoolMapSearchUrl, makeSearchURL } from '../../common/searchRequestUtils';
import { extractSuggestionLocationParameters } from '../../common/locationUtils';
import { RecurringDonationSchoolCard } from './RecurringDonationSchoolCard';
import { Svg } from '../../common/Svg';

interface ChooseBoxesProps {
    designatedSchool: SchoolSuggestion | null,
    handleSchoolOrTeacherSelect: (selection: { schoolSuggestion: SchoolSuggestion | null; teacherSuggestion: TeacherSuggestion | null }) => void,
    setErrorMessage: (message: string | null) => void;
}

const MAX_LOCATION_BASED_SCHOOL_RESULTS = 25;
const SEARCH_URL_MAP = '/donors/search.html?schoolsOnly=true&map=true';
const baseSearchURL = baseFrontURL + 'donors/search.html';
const SCHOOLS_PER_PAGE = 5;

const getSearchParams = function (location: GeoLocationPlace) {
    const params = extractSuggestionLocationParameters(location);
    params.centerLat = params.centerLat || String(location.latLng?.lat ?? '');
    params.centerLng = params.centerLng || String(location.latLng?.lng ?? '');
    return params;
};

export const RecurringDonationLandingSchoolChooseBox = function ({
    designatedSchool,
    handleSchoolOrTeacherSelect,
    setErrorMessage,
} : ChooseBoxesProps) {
    const [location, setLocation] = React.useState<GeoLocationPlace | null>(null);
    const [schools, setSchools] = React.useState<SchoolSuggestion[] | 'LOADING'>([]);
    const [schoolCards, setSchoolCards] = React.useState<React.JSX.Element[]>([]);
    const [currentPage, setCurrentPage] = React.useState(0);
    const [searchByLocation, setSearchByLocation] = React.useState(false);

    const getSchoolSuggestions = function (location: GeoLocationPlace) {
        const params = getSearchParams(location);
        const schoolSearchUrl = convertToSchoolMapSearchUrl(makeSearchURL(params, SEARCH_URL_MAP), MAX_LOCATION_BASED_SCHOOL_RESULTS);
        setSchools('LOADING');
        $.ajax({
            type: 'GET',
            url: schoolSearchUrl,
            cache: true,
            dataType: 'json',
        }).done((schoolResults: SchoolSuggestion[]) => {
            setSchools(schoolResults);
        }).fail(() => {
            setSchools([]);
        });
    };

    const searchZipCode = function (zipCode: string) {
        $.ajax({
            url: '/common/zipLookup.html?zip=' + zipCode + '&APIKey=' + dcAPIKey,
            dataType: 'json',
        }).done((data) => {
            if (data) {
                const locationParams = {
                    zip: data.zip,
                    cityName: data.city,
                    stateCode: data.state,
                    latLng: { lat: data.latitude, lng: data.longitude },
                };
                setLocation(locationParams);
                getSchoolSuggestions(locationParams);
            } else {
                setErrorMessage('There was an error retrieving this location. Please try again.');
            }
        }).fail(() => {
            setErrorMessage('There was an error retrieving this location. Please try again.');
        });
    };

    React.useEffect(() => {
        if (schools.length > 0 && schools !== 'LOADING') {
            const newSchoolCards: React.JSX.Element[] = [];
            const startIndex = currentPage * SCHOOLS_PER_PAGE;
            const endIndex = startIndex + SCHOOLS_PER_PAGE;
            const paginatedSchools = schools.slice(startIndex, endIndex);
            for (const school of paginatedSchools) {
                newSchoolCards.push(
                    <button key={`button-school-${school.schoolId}`} onClick={() => handleSchoolOrTeacherSelect({
                        schoolSuggestion: school,
                        teacherSuggestion: null,
                    })}>
                        <RecurringDonationSchoolCard
                            school={school}
                        />
                    </button>,
                );
            }
            setSchoolCards(newSchoolCards);
        }
    }, [schools, currentPage, handleSchoolOrTeacherSelect]);

    if (designatedSchool) {
        return (
            <div className='designated-school-selection'>
                <div className='school-card'>
                    <div className='school-location'>
                        <span className='location'><Svg icon='location' />{designatedSchool.cityName}, {designatedSchool.stateCode}</span>
                    </div>
                    <h3 className='school-name'>
                        {designatedSchool.schoolName}
                    </h3>
                    <button className='text-link' onClick={ () => handleSchoolOrTeacherSelect({ schoolSuggestion: null, teacherSuggestion: null }) }>Change school</button>
                </div>
            </div>
        );
    }
    const getNameSearchCallout = () => {
        return (
            <StandaloneSearchBoxes
                searchURL={baseSearchURL}
                baseSearchURL={baseSearchURL}
                detectedLocation={location}
                onSchoolOrTeacherSelect={handleSchoolOrTeacherSelect}
                skipKeywordsSearch={true}
                skipDistrictSearch={true}
                hideZipCodeSearch={true}
                skipSchoolSearch={false}
                skipTeacherSearch={true}
                placeholder={'Search for a school'}
            />
        );
    };

    const getSchoolLocationSearchCallout = () => {
        const showCollapsedLocation = location && searchByLocation;
        let schoolCardsList = schoolCards.length > 0 ? <ul className='school-suggestion-list'>{schoolCards}</ul> : null;
        if (schools.length > 5) {
            const isLastPage = (currentPage + 1) * SCHOOLS_PER_PAGE >= schools.length;
            const isFirstPage = currentPage === 0;
            schoolCardsList =
                <>
                    { schoolCardsList }
                    <div className='pagination-controls'>
                        <button
                            className={(isFirstPage ? 'inactive-pagination-link ' : '') + 'text-link js-click-on-enter-or-space'}
                            onClick={() => setCurrentPage(prev => prev - 1)}
                            disabled={currentPage === 0}
                            type='button'
                            tabIndex={0}>
                            Previous
                        </button>
                        <span className={'page-count'}>Page {currentPage + 1}</span>
                        <button
                            className={(isLastPage ? 'inactive-pagination-link ' : '') + 'text-link js-click-on-enter-or-space'}
                            onClick={() => setCurrentPage(prev => prev + 1)}
                            disabled={isLastPage}
                            type='button'
                            tabIndex={0}>
                            Next
                        </button>
                    </div>
                    { isLastPage && location &&
                        <a className='button-secondary small js-click-on-enter-or-space search-schools-link' href={baseSearchURL + '?centerZip=' + location?.zip} tabIndex={0}>
                            Search for more schools
                        </a>}
                </>;
        }
        if (showCollapsedLocation) {
            return (
                <>
                    <div className='collapsed-location-view'>
                        <span className='location-text'>{location.zip}</span>
                        <button
                            className='text-link js-click-on-enter-or-space change-location-button'
                            onClick={() => {
                                setLocation(null);
                                setSchools([]);
                            }}
                            type='button'
                            tabIndex={0}>
                            Change location
                        </button>
                    </div>
                    {schoolCardsList}
                </>
            );
        }
        return (
            <>
                <div className='form-redux'>
                    <SchoolZipCodeSearch
                        showZipCodeSearch={true}
                        hideZipCodeSearch={() => setSearchByLocation(false)}
                        searchZipCode={searchZipCode}
                        skipAutofocus={false}
                    />
                </div>
                {schoolCardsList}
            </>
        );
    };

    return (
        <div>
            { searchByLocation
                ? getSchoolLocationSearchCallout()
                : getNameSearchCallout()}
            <div className='change-location-button-container'>
                <button className='text-link' onClick={() => setSearchByLocation(!searchByLocation)} type='button'>
                    Find a school by {searchByLocation ? 'name' : 'zipcode'}
                </button>
            </div>
        </div>
    );
};
